import { useEffect, useState } from 'react';
import { Badge, fmtDateTime } from '../atoms';
import { getPlanningAssessment, type PlanningAssessment } from '../api/planningAssessments';
import { getPlanningDocumentDownloadUrl } from '../api/planningDocuments';
import { conversationFor, type ConversationCheck } from '../api/descriptionConversation';
import { errMessage, fmtCost, fmtTokens, renderMarkdown } from '../pageUtils';

// Every assessment run on a case, newest first, for administrators: when, how it ended, what it cost and, for a pre-application
// check, exactly which descriptions were sent to the model with it. Each run's analysis loads only when it is opened.

type Run = ConversationCheck & Pick<PlanningAssessment, 'score' | 'riskBand' | 'assessedAt' | 'inputTokens' | 'outputTokens' | 'estimatedCostUsd'>;

const LABEL: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', margin: '0 0 6px' };
const MUTED: React.CSSProperties = { color: 'var(--ink-3)', fontSize: 12.5 };

function statusBadge(status: string) {
  const kind = status === 'Completed' ? 'pos' : status === 'Failed' ? 'neg' : status === 'Cancelled' ? 'neutral' : 'warn';
  const label = status === 'AwaitingReply' ? 'Awaiting reply' : status;
  return <Badge kind={kind}>{label}</Badge>;
}

function RunDetail({ run, runs, preApplication }: { run: Run; runs: Run[]; preApplication: boolean }) {
  const [full, setFull] = useState<PlanningAssessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getPlanningAssessment(run.id)
      .then(a => { if (!cancelled) setFull(a ?? null); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [run.id]);

  async function openPdf() {
    if (!full?.pdf) return;
    setPdfError(null);
    try {
      window.open(await getPlanningDocumentDownloadUrl(full.pdf.id), '_blank', 'noopener');
    } catch (e) {
      setPdfError(errMessage(e, 'Could not open the report.'));
    }
  }

  const sent = conversationFor(runs, run.id);
  return (
    <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={LABEL}>{preApplication ? 'Descriptions sent to the model' : 'Description'}</div>
        {!preApplication ? (
          <div style={MUTED}>An application is assessed from its documents; no description is sent.</div>
        ) : !sent.length ? (
          <div style={MUTED}>Not recorded: this run was made before descriptions were kept with each run.</div>
        ) : (
          <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sent.map((m, i) => (
              <li key={m.id} style={{ fontSize: 12.5 }}>
                <span style={{ color: 'var(--ink-3)' }}>
                  {i === sent.length - 1 ? 'This run' : i === 0 ? 'Earlier description' : 'Earlier refinement'}
                  {i === sent.length - 1 && sent.length > 1 ? ' (refinement)' : ''} · {fmtDateTime(m.at)}
                </span>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{m.text}</div>
              </li>
            ))}
          </ol>
        )}
      </div>
      {error ? (
        <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>Failed to load this run: {error}</div>
      ) : loading || !full ? (
        <span className="skel" style={{ width: 220 }} />
      ) : (
        <>
          {full.error && <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>{full.error}</div>}
          {full.pdf && (
            <div><button type="button" className="btn" onClick={() => void openPdf()}>Download this report (PDF)</button></div>
          )}
          {pdfError && <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>{pdfError}</div>}
          {full.analysis ? (
            <details>
              <summary style={{ ...LABEL, cursor: 'pointer', margin: 0 }}>Analysis</summary>
              <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(full.analysis) }} />
            </details>
          ) : (
            <div style={MUTED}>No analysis text for this run.</div>
          )}
        </>
      )}
    </div>
  );
}

export function AssessmentHistory({ runs, preApplication }: { runs: Run[]; preApplication: boolean }) {
  const sorted = [...runs].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const [open, setOpen] = useState<Set<string>>(new Set());
  if (!sorted.length) {
    return <div className="card"><div className="card-body" style={{ padding: 32, textAlign: 'center', ...MUTED }}>No assessment has been run for this case yet.</div></div>;
  }
  return (
    <div className="card">
      <div className="card-hd">Assessment history<span className="card-hd-sub">{sorted.length} run{sorted.length === 1 ? '' : 's'}, newest first</span></div>
      <div className="card-body flush">
        {sorted.map(run => (
          <details
            key={run.id} style={{ borderTop: '1px solid var(--line)' }}
            onToggle={e => { const isOpen = e.currentTarget.open; setOpen(prev => { const next = new Set(prev); if (isOpen) next.add(run.id); else next.delete(run.id); return next; }); }}
          >
            {/* The same two lines for every run, whatever it holds, so the list reads as a column: when, how it ended and what it
                cost; then its result and, for a pre-application check, what kind of description it was given. */}
            <summary style={{ cursor: 'pointer', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="mono">{fmtDateTime(run.assessedAt ?? run.createdAt)}</span>
                {statusBadge(run.status)}
                <span className="hide-mobile" style={{ color: 'var(--ink-3)', marginLeft: 'auto' }}>
                  {fmtTokens(run.inputTokens)} in / {fmtTokens(run.outputTokens)} out · {fmtCost(run.estimatedCostUsd)}
                </span>
              </span>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
                <span>{run.riskBand ? `${run.riskBand}${run.score != null ? ` · ${run.score}/100` : ''}` : '—'}</span>
                {preApplication && run.proposalDescription && run.proposalRefines != null && <span style={{ color: 'var(--ink-3)' }}>{run.proposalRefines ? 'Refinement' : 'New description'}</span>}
              </span>
            </summary>
            {open.has(run.id) && <RunDetail run={run} runs={runs} preApplication={preApplication} />}
          </details>
        ))}
      </div>
    </div>
  );
}
